import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SiteMap } from '@/components/sites/SiteMap';
import { useSiteStore } from '@/stores/siteStore';
import { useRisks, useTasks, useControls, useIncidents, useEvidence, useSuppliers } from '@/hooks/queries';
import { 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle, 
  Users, 
  Plus,
  FileText,
  Shield,
  Clock,
  ClipboardList,
  Folder,
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { ControlCreateDialog } from '@/components/controls/ControlCreateDialog';
import { RiskCreateDialog } from '@/components/risks/RiskCreateDialog';
import { IncidentCreateDialog } from '@/components/incidents/IncidentCreateDialog';
import { EvidenceCreateDialog } from '@/components/evidence/EvidenceCreateDialog';
import { SupplierCreateDialog } from '@/components/suppliers/SupplierCreateDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TaskCadence } from '@/types/tasks';
import dayjs from 'dayjs';

export default function DashboardPage() {
  const { profile, organization } = useAuth();
  const { sites, loadSites } = useSiteStore();
  const { risks } = useRisks(organization?.id);
  const { tasks, createTask } = useTasks(organization?.id);
  const { createControl, isCreating: isCreatingControl } = useControls(organization?.id);
  const { createRisk, isCreating: isCreatingRisk } = useRisks(organization?.id);
  const { createIncident } = useIncidents(organization?.id);
  const { createEvidence } = useEvidence(organization?.id);
  const { createSupplier, isCreating: isCreatingSupplier } = useSuppliers(organization?.id);
  const navigate = useNavigate();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Fetch compliance statistics
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      const [controlsResult, risksResult, incidentsResult, suppliersResult] = await Promise.all([
        supabase.from('controls').select('status').eq('org_id', organization.id),
        supabase.from('risks').select('status, risk_score').eq('org_id', organization.id),
        supabase.from('incidents').select('status, priority').eq('org_id', organization.id),
        supabase.from('suppliers').select('criticality').eq('org_id', organization.id),
      ]);

      if (suppliersResult.error) console.error('Suppliers error:', suppliersResult.error);

      const controls = controlsResult.data || [];
      const risks = risksResult.data || [];
      const incidents = incidentsResult.data || [];
      const suppliers = suppliersResult.data || [];

      // Calculate compliance score
      const implementedControls = controls.filter(c => c.status === 'implemented').length;
      const totalControls = controls.length;
      const complianceScore = totalControls > 0 ? Math.round((implementedControls / totalControls) * 100) : 0;

      // Count open gaps
      const openGaps = controls.filter(c => c.status === 'not_implemented').length;

      // Count top risks (high score)
      const topRisks = risks.filter(r => r.risk_score && r.risk_score >= 15).length;

      // Count open incidents
      const openIncidents = incidents.filter(i => i.status !== 'resolved').length;

      // Count critical suppliers
      const criticalSuppliers = suppliers.filter(s => s.criticality === 'critical').length;

      return {
        complianceScore,
        openGaps,
        topRisks,
        openIncidents,
        criticalSuppliers,
        totalControls,
        totalRisks: risks.length,
        totalIncidents: incidents.length,
        totalSuppliers: suppliers.length,
      };
    },
    enabled: !!organization?.id,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Overview</h1>
          <p className="text-muted-foreground">
            Welcome back, {profile?.name || profile?.email}
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu open={quickAddOpen} onOpenChange={setQuickAddOpen}>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Quick Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setTaskDialogOpen(true); }}>
                <ClipboardList className="h-4 w-4 mr-2" />
                New Task
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setControlDialogOpen(true); }}>
                <Shield className="h-4 w-4 mr-2" />
                New Control
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setRiskDialogOpen(true); }}>
                <AlertTriangle className="h-4 w-4 mr-2" />
                New Risk
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setIncidentDialogOpen(true); }}>
                <AlertCircle className="h-4 w-4 mr-2" />
                Log Incident
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setEvidenceDialogOpen(true); }}>
                <Folder className="h-4 w-4 mr-2" />
                Upload Evidence
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setQuickAddOpen(false); setSupplierDialogOpen(true); }}>
                <Users className="h-4 w-4 mr-2" />
                Add Supplier
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.complianceScore || 0}%</div>
            <Progress value={stats?.complianceScore || 0} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.totalControls || 0} total controls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Open Gaps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{stats?.openGaps || 0}</div>
            <div className="flex items-center mt-1">
              <AlertTriangle className="h-4 w-4 text-warning mr-1" />
              <p className="text-xs text-muted-foreground">Needs attention</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Top Risks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats?.topRisks || 0}</div>
            <div className="flex items-center mt-1">
              <AlertTriangle className="h-4 w-4 text-destructive mr-1" />
              <p className="text-xs text-muted-foreground">High priority</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Open Incidents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.openIncidents || 0}</div>
            <div className="flex items-center mt-1">
              <AlertCircle className="h-4 w-4 text-primary mr-1" />
              <p className="text-xs text-muted-foreground">In progress</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Critical Suppliers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.criticalSuppliers || 0}</div>
            <div className="flex items-center mt-1">
              <Users className="h-4 w-4 text-primary mr-1" />
              <p className="text-xs text-muted-foreground">Require review</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Site Map Widget */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Site Locations</CardTitle>
            <CardDescription>
              Geographic overview of your sites ({sites.length} total)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative h-[400px]">
              <SiteMap sites={sites} />
            </div>
            
            {/* Overlay message when no sites */}
            {sites.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
                <p className="text-muted-foreground mb-2">No sites created yet</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/sites')}
                >
                  Create First Site
                </Button>
              </div>
            )}
            
            {/* Overlay when sites exist but no coordinates */}
            {sites.length > 0 && !sites.some(s => s.metadata?.coordinates?.lat && s.metadata?.coordinates?.lon) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
                <p className="text-muted-foreground mb-2">Sites found, but no coordinates set</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/sites')}
                >
                  Add Site Locations
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risk & Task Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Top Risks
            </CardTitle>
            <CardDescription>
              Highest priority risks requiring attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            {risks && risks.filter(r => r.risk_score && r.risk_score >= 10).slice(0, 5).length > 0 ? (
              <div className="space-y-3">
                {risks
                  .filter(r => r.risk_score && r.risk_score >= 10)
                  .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))
                  .slice(0, 5)
                  .map((risk) => (
                    <div key={risk.id} className="flex items-start justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{risk.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{risk.cause}</p>
                      </div>
                      <Badge variant={risk.risk_score && risk.risk_score >= 15 ? 'destructive' : 'secondary'}>
                        {risk.risk_score}
                      </Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No high-priority risks</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Upcoming Tasks
            </CardTitle>
            <CardDescription>
              Tasks due soon or overdue
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(tasks ?? []).filter(t => t.status !== 'Done').slice(0, 5).length > 0 ? (
              <div className="space-y-3">
                {(tasks ?? [])
                  .filter(t => t.status !== 'Done')
                  .slice(0, 5)
                  .map((task) => (
                    <div key={task.id} className="flex items-start justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{task.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {new Date(task.due_next).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={task.status === 'Overdue' ? 'destructive' : 'secondary'}>
                        {task.status}
                      </Badge>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No pending tasks</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>
            Latest updates across your compliance program
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
            <p className="text-sm">Activity will appear here as you use the system</p>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialogs */}
      <ControlCreateDialog
        open={controlDialogOpen}
        onOpenChange={setControlDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createControl({ ...data, org_id: organization.id });
          setControlDialogOpen(false);
        }}
        isCreating={isCreatingControl}
      />

      <RiskCreateDialog
        open={riskDialogOpen}
        onOpenChange={setRiskDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createRisk({ ...data, org_id: organization.id });
          setRiskDialogOpen(false);
        }}
        isCreating={isCreatingRisk}
      />

      <IncidentCreateDialog
        open={incidentDialogOpen}
        onOpenChange={setIncidentDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createIncident({ ...data, org_id: organization.id });
          setIncidentDialogOpen(false);
        }}
      />

      <EvidenceCreateDialog
        open={evidenceDialogOpen}
        onOpenChange={setEvidenceDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createEvidence({ ...data, org_id: organization.id });
          setEvidenceDialogOpen(false);
        }}
      />

      <SupplierCreateDialog
        open={supplierDialogOpen}
        onOpenChange={setSupplierDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createSupplier({ ...data, org_id: organization.id });
          setSupplierDialogOpen(false);
        }}
        isCreating={isCreatingSupplier}
      />

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        onSave={(data) => {
          if (!organization?.id) return;
          createTask({
            ...data,
            org_id: organization.id,
            status: 'Scheduled',
          });
          setTaskDialogOpen(false);
        }}
      />
    </div>
  );
}

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  cadence: z.enum(['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom_rrule']),
  rrule: z.string().optional(),
  owner: z.string().optional(),
  due_date: z.string().min(1, 'Due date is required'),
});

type TaskFormData = z.infer<typeof taskSchema>;

function TaskDialog({ open, onOpenChange, onSave }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: { title: string; description?: string; cadence: string; rrule?: string; owner?: string; due_next: string }) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      cadence: 'monthly',
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    },
  });

  const watchedCadence = watch('cadence');

  const handleTaskSubmit = (data: TaskFormData) => {
    onSave({
      title: data.title,
      description: data.description,
      cadence: data.cadence,
      rrule: data.rrule,
      owner: data.owner,
      due_next: dayjs(data.due_date).toISOString(),
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Task</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(handleTaskSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter task title"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Describe the task"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cadence">Cadence *</Label>
              <Select
                value={watchedCadence}
                onValueChange={(value) => setValue('cadence', value as TaskCadence)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="custom_rrule">Custom (RRULE)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date">Next Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                {...register('due_date')}
              />
              {errors.due_date && (
                <p className="text-sm text-destructive">{errors.due_date.message}</p>
              )}
            </div>
          </div>

          {watchedCadence === 'custom_rrule' && (
            <div>
              <Label htmlFor="rrule">RRULE</Label>
              <Input
                id="rrule"
                {...register('rrule')}
                placeholder="FREQ=MONTHLY;INTERVAL=1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a valid RRULE string for custom recurrence patterns
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="owner">Owner</Label>
            <Input
              id="owner"
              {...register('owner')}
              placeholder="Person or team responsible"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => {
              reset();
              onOpenChange(false);
            }}>
              Cancel
            </Button>
            <Button type="submit">
              Create Task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}